import { useState } from "react";
import { Link, useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ShieldCheck } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import Logo from "@/components/Logo";
import { useAuth } from "@/lib/auth";
import { apiError } from "@/lib/api";

export default function LoginPage() {
  const loc = useLocation();
  const isAdmin = loc.pathname.startsWith("/admin/login");
  const { login } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const data = await login(email, password);
      if (isAdmin && data.role !== "admin") {
        toast.error("This account isn't an admin.");
        return;
      }
      toast.success(`Welcome back, ${data.name}`);
      nav(data.role === "admin" ? "/admin" : (params.get("next") || "/dashboard"));
    } catch (e) { toast.error(apiError(e, "Login failed")); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-5 py-16">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex"><Logo size={40} /></div>
        </div>
        <Card className="p-8">
          <div className="text-xs uppercase tracking-[0.2em] text-eagle-gold font-semibold mb-2">
            {isAdmin ? "Staff area" : "Customer portal"}
          </div>
          <h1 className="font-display text-3xl font-bold mb-6">{isAdmin ? "Admin sign in" : "Welcome back"}</h1>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label>Email</Label>
              <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email" />
            </div>
            <div>
              <Label>Password</Label>
              <Input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password" />
            </div>
            <Button type="submit" disabled={busy} className="w-full h-11 bg-eagle-gold text-eagle-ink hover:bg-eagle-goldHover font-semibold" data-testid="login-submit">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}
            </Button>
          </form>
          {!isAdmin && (
            <div className="mt-6 text-sm text-center text-muted-foreground">
              New here? <Link to="/register" className="text-eagle-gold hover:underline" data-testid="login-goto-register">Create an account</Link>
            </div>
          )}
          {isAdmin && (
            <div className="mt-6 text-xs text-center text-muted-foreground flex items-center justify-center gap-1">
              <ShieldCheck className="h-3 w-3" /> Encrypted · Restricted to Eagle Eye staff
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
